import { normalizeBlockedActions } from '../modules/warehouse/blockedActions';
import type {
  OrderProductRealizationDto,
  OrderTransportsDto,
  RouteOrderDto,
  SupplierOrderTransportDto,
  TransportCargoItemDto,
  TransportOrderSummaryDto,
  TransportProductDto,
  TransportRouteDetailDto,
  TransportRouteStatus,
  TransportRouteSummaryDto,
  TransportSupplierOrderSummaryDto,
} from '../types/transport';

import {
  asArray,
  asBoolean,
  asNumber,
  asOptionalArray,
  asOptionalString,
  asRecord,
  asString,
  reportDtoAnomaly,
} from './common';
import { normalizeCustomer, normalizeUser } from './order';
import { getDisplayOrderNumber } from '../types/order';
import { normalizeTransportAddressList } from '../utils/transportAddressUtils';
import {
  TRANSPORT_COST_ALLOCATION_METHODS,
  type AllocationMethod as TransportCostAllocationMethod,
  type TransportCostAllocationResult,
  type TransportCostAllocationWarningCode,
} from '../utils/transportCostAllocation';

export type {
  OrderProductRealizationDto,
  OrderTransportsDto,
  RouteOrderDto,
  SupplierOrderTransportDto,
  TransportCargoItemDto,
  TransportOrderSummaryDto,
  TransportProductDto,
  TransportRouteDetailDto,
  TransportRouteStatus,
  TransportRouteSummaryDto,
  TransportSupplierOrderSummaryDto,
} from '../types/transport';

const ROUTE_STATUSES: TransportRouteStatus[] = [
  'PENDING',
  'SCHEDULED',
  'LOADING',
  'IN_TRANSIT',
  'DELIVERED',
  'CANCELLED',
  'PLANNED',
  'DELAYED',
];

export function normalizeTransportRouteSummary(
  raw: unknown,
  entity = 'transportRoute',
): TransportRouteSummaryDto {
  const record = asRecord(raw);

  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }

  const routeOrders = normalizeRouteOrders(record?.routeOrders, `${entity}.routeOrders`);
  const fallbackOrder = routeOrders.length === 0
    ? normalizeRouteOrderFallback(record, `${entity}.fallbackOrder`)
    : null;
  const effectiveRouteOrders = routeOrders.length > 0 ? routeOrders : fallbackOrder ? [fallbackOrder] : [];
  const firstOrder = effectiveRouteOrders[0]?.order;
  const products = normalizeTransportProducts(record?.products, `${entity}.products`);

  return {
    warehouseDocuments: asOptionalArray(record?.warehouseDocuments, value => value)?.flatMap((raw) => {
      const document = asRecord(raw);
      if (!document || !document.id || !['pz', 'wz'].includes(asString(document.kind))) return [];
      return [{ id: asString(document.id), kind: document.kind as 'pz' | 'wz',
        number: asString(document.number), status: asString(document.status),
        orderId: asOptionalString(document.orderId), orderNumber: asOptionalString(document.orderNumber) }];
    }),
    id: asString(record?.id),
    routeNumber: asString(record?.routeNumber),
    originCity: asString(record?.originCity),
    originCountry: asString(record?.originCountry),
    destinationCity: asString(record?.destinationCity),
    destinationCountry: asString(record?.destinationCountry),
    departureDate: asString(record?.departureDate),
    estimatedArrival: asString(record?.estimatedArrival),
    actualArrival: asOptionalString(record?.actualArrival),
    transportCompany: asString(record?.transportCompany),
    transportCompanyId: asOptionalString(record?.transportCompanyId) ?? null,
    transportContact: asOptionalString(record?.transportContact),
    vehicleRegistration: asOptionalString(record?.vehicleRegistration),
    driverName: asOptionalString(record?.driverName),
    driverPhone: asOptionalString(record?.driverPhone),
    transportCost: asNumber(record?.transportCost),
    transportRate: record?.transportRate == null ? undefined : asNumber(record?.transportRate),
    currency: asString(record?.currency, 'PLN'),
    costAllocationMethod: normalizeTransportCostAllocationMethod(record?.costAllocationMethod),
    costAllocation: normalizeTransportCostAllocation(record?.costAllocation, `${entity}.costAllocation`),
    invoiceNumber: asOptionalString(record?.invoiceNumber),
    status: normalizeTransportStatus(record?.status, `${entity}.status`),
    settlementLocked: asBoolean(record?.settlementLocked),
    // Backend-computed "why is this unavailable" (never re-derived in pages).
    blockedActions: normalizeBlockedActions(record?.blockedActions),
    notes: asOptionalString(record?.notes),
    orderId: asOptionalString(record?.orderId) || effectiveRouteOrders[0]?.orderId,
    orderNumber: asOptionalString(record?.orderNumber) || firstOrder?.orderNumber || '',
    displayOrderNumber:
      asOptionalString(record?.displayOrderNumber)
      || firstOrder?.displayOrderNumber
      || asOptionalString(record?.orderNumber)
      || firstOrder?.orderNumber
      || '',
    customerName: pickTransportCustomerName(record, firstOrder),
    customer: asOptionalString(record?.customer),
    products: products.length > 0 ? products : firstOrder?.products || [],
    routeOrders: effectiveRouteOrders,
    // Etap 2: absent on a pre-migration-128 backend (and on any transport with
    // no inbound leg) — defaults to [] rather than reporting an anomaly, so an
    // older response degrades to "no supplier orders" instead of noise.
    supplierOrders: normalizeTransportSupplierOrders(
      record?.supplierOrders,
      `${entity}.supplierOrders`,
    ),
    cargoItems: normalizeTransportCargoItems(record?.cargoItems, `${entity}.cargoItems`),
    loadingDate: asOptionalString(record?.loadingDate),
    estimatedDelivery: asOptionalString(record?.estimatedDelivery),
    actualDelivery: asOptionalString(record?.actualDelivery),
    loadingAddresses: normalizeTransportAddressList(record?.loadingAddresses),
    deliveryAddresses: normalizeTransportAddressList(record?.deliveryAddresses),
    dimensions: asOptionalString(record?.dimensions),
    licensePlate1: asOptionalString(record?.licensePlate1),
    licensePlate2: asOptionalString(record?.licensePlate2),
    totalWeight: record?.totalWeight == null ? undefined : asNumber(record?.totalWeight),
    totalVolume: record?.totalVolume == null ? undefined : asNumber(record?.totalVolume),
    packages: record?.packages == null ? undefined : asNumber(record?.packages),
    createdAt: asString(record?.createdAt),
    updatedAt: asString(record?.updatedAt),
  };
}

export function normalizeTransportRoutes(
  raw: unknown,
  entity = 'transportRoutes',
): TransportRouteSummaryDto[] {
  return asArray(raw, (item, index) => normalizeTransportRouteSummary(item, `${entity}[${index}]`));
}

export function normalizeTransportRouteDetail(
  raw: unknown,
  entity = 'transportRouteDetail',
): TransportRouteDetailDto {
  const summary = normalizeTransportRouteSummary(raw, entity);
  const record = asRecord(raw);
  const createdBy = normalizeUser(record?.createdBy, `${entity}.createdBy`);

  return {
    ...summary,
    createdByName: createdBy?.name || '',
    createdByEmail: createdBy?.email || '',
  };
}

export function normalizeOrderTransports(
  raw: unknown,
  entity = 'orderTransports',
): OrderTransportsDto {
  const record = asRecord(raw);

  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }

  return {
    transports: asArray(record?.transports, (item, index) =>
      normalizeTransportRouteDetail(item, `${entity}.transports[${index}]`),
    ),
    productRealization: asArray(record?.productRealization, (item, index) =>
      normalizeOrderProductRealization(item, `${entity}.productRealization[${index}]`),
    ),
  };
}

function normalizeOrderProductRealization(
  raw: unknown,
  entity: string,
): OrderProductRealizationDto {
  const record = asRecord(raw);

  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }

  return {
    orderProductId: asString(record?.orderProductId),
    name: asString(record?.name),
    unit: asOptionalString(record?.unit) ?? null,
    commercialUnit: asOptionalString(record?.commercialUnit) ?? null,
    orderedCommercialQuantity: record?.orderedCommercialQuantity == null ? null : asNumber(record.orderedCommercialQuantity),
    allocatedCommercialQuantity: record?.allocatedCommercialQuantity == null ? null : asNumber(record.allocatedCommercialQuantity),
    remainingCommercialQuantity: record?.remainingCommercialQuantity == null ? null : asNumber(record.remainingCommercialQuantity),
    orderedQuantity: asNumber(record?.orderedQuantity),
    allocatedQuantity: asNumber(record?.allocatedQuantity),
    remainingQuantity: asNumber(record?.remainingQuantity),
    percent: asNumber(record?.percent),
    deliveredQuantity: asNumber(record?.deliveredQuantity),
    deliveryPercent: asNumber(record?.deliveryPercent),
  };
}

function normalizeRouteOrders(raw: unknown, entity: string): RouteOrderDto[] {
  return asArray(raw, (item, index) => {
    const record = asRecord(item);
    const order = normalizeTransportOrderSummary(record?.order, `${entity}[${index}].order`);

    return {
      id: asString(record?.id) || asString(record?.orderId) || order.id,
      orderId: asString(record?.orderId) || order.id,
      loadingAddress: asOptionalString(record?.loadingAddress) ?? null,
      deliveryAddress: asOptionalString(record?.deliveryAddress) ?? null,
      allocationMethod: asOptionalString(record?.allocationMethod) ?? undefined,
      allocationBasisValue: record?.allocationBasisValue == null ? undefined : asNumber(record?.allocationBasisValue),
      allocationPercent: record?.allocationPercent == null ? undefined : asNumber(record?.allocationPercent),
      allocatedTransportCost: record?.allocatedTransportCost == null ? undefined : asNumber(record?.allocatedTransportCost),
      // P8: null/undefined means "no conversion happened" (plain-PLN
      // transport) — never coerce to a guessed currency.
      originalCurrency: asOptionalString(record?.originalCurrency) ?? null,
      originalAmount: record?.originalAmount == null ? null : asNumber(record?.originalAmount),
      exchangeRateUsed: record?.exchangeRateUsed == null ? null : asNumber(record?.exchangeRateUsed),
      exchangeRateSource: asOptionalString(record?.exchangeRateSource) ?? null,
      exchangeRateTableNumber: asOptionalString(record?.exchangeRateTableNumber) ?? null,
      exchangeRateRequestedDate: asOptionalString(record?.exchangeRateRequestedDate) ?? null,
      exchangeRateEffectiveDate: asOptionalString(record?.exchangeRateEffectiveDate) ?? null,
      exchangeRateIsFallback: record?.exchangeRateIsFallback == null ? null : asBoolean(record.exchangeRateIsFallback),
      cargoDescription: asOptionalString(record?.cargoDescription),
      packages: record?.packages == null ? undefined : asNumber(record?.packages),
      weight: record?.weight == null ? undefined : asNumber(record?.weight),
      volume: record?.volume == null ? undefined : asNumber(record?.volume),
      areaSqm: record?.areaSqm == null ? undefined : asNumber(record?.areaSqm),
      ldm: record?.ldm == null ? undefined : asNumber(record?.ldm),
      productRealization: asArray(record?.productRealization, (item, realizationIndex) =>
        normalizeOrderProductRealization(
          item,
          `${entity}[${index}].productRealization[${realizationIndex}]`,
        )
      ),
      order,
    };
  });
}

function normalizeRouteOrderFallback(record: ReturnType<typeof asRecord>, entity: string): RouteOrderDto | null {
  if (!record || !record.orderId) {
    return null;
  }

  return {
    id: asString(record.orderId),
    orderId: asString(record.orderId),
    productRealization: [],
    order: normalizeTransportOrderSummary(record, `${entity}.order`),
  };
}

function normalizeTransportOrderSummary(raw: unknown, entity: string): TransportOrderSummaryDto {
  const record = asRecord(raw);
  const customer = normalizeCustomer(record?.customer, `${entity}.customer`);
  const orderNumber = asString(record?.orderNumber);
  const manualOrderNumber = asOptionalString(record?.manualOrderNumber) ?? null;
  const useManualNumbering = record?.useManualNumbering === true;

  return {
    id: asString(record?.id),
    orderNumber,
    manualOrderNumber,
    useManualNumbering,
    displayOrderNumber:
      asOptionalString(record?.displayOrderNumber)
      || getDisplayOrderNumber({ orderNumber, manualOrderNumber, useManualNumbering }),
    factoryLocation: asOptionalString(record?.factoryLocation) ?? null,
    deliveryLocation: asOptionalString(record?.deliveryLocation) ?? null,
    clientOrderNumber: asOptionalString(record?.clientOrderNumber) ?? null,
    customerName: customer?.name || asOptionalString(record?.customerName) || '',
    revenue: asNumber(record?.revenue),
    currency: asString(record?.currency, 'PLN'),
    status: asString(record?.status),
    products: normalizeTransportProducts(record?.products, `${entity}.products`),
    customer,
  };
}

/**
 * Etap 2 (transport przywozowy): supplier orders (ZD) linked to a transport.
 * The backend already resolves the canonical display number
 * (buildSupplierOrderDisplayNumberSql), so this only falls back to the raw
 * `orderNumber` when an older response omits it.
 */
export function normalizeTransportSupplierOrders(
  raw: unknown,
  entity = 'transportSupplierOrders',
): TransportSupplierOrderSummaryDto[] {
  return asArray(raw, (item, index) => {
    const record = asRecord(item);

    if (!record) {
      reportDtoAnomaly(`${entity}[${index}]`, 'root', item);
    }

    const orderNumber = asString(record?.orderNumber);

    return {
      id: asString(record?.id),
      orderNumber,
      displayOrderNumber: asOptionalString(record?.displayOrderNumber) || orderNumber,
      supplierName: asOptionalString(record?.supplierName) ?? null,
    };
  }).filter((supplierOrder) => Boolean(supplierOrder.id));
}

/**
 * Etap 2: narrow a full transport (as embedded in `PZDocument.sourceTransports`)
 * down to the trimmed picker shape. Lets a saved PZ render its own linked
 * transports through the same component as the supplier-order candidate list,
 * instead of a second, near-identical row renderer.
 *
 * `pzNumbers` is deliberately empty here: on a PZ document the "which PZs came
 * off this truck" question is already answered by the document being looked at.
 */
export function mapTransportSummaryToSupplierOrderTransport(
  transport: TransportRouteSummaryDto,
): SupplierOrderTransportDto {
  // The full transport DTO stores "no value" as '' for some fields and
  // undefined for others; the picker shape says null once, so an absent plate
  // or date renders a single honest "—" instead of an empty chip.
  const toNullableText = (value: string | null | undefined): string | null =>
    (typeof value === 'string' && value.trim() ? value : null);

  return {
    id: transport.id,
    routeNumber: transport.routeNumber,
    transportCompany: transport.transportCompany,
    loadingDate: toNullableText(transport.loadingDate),
    departureDate: toNullableText(transport.departureDate),
    licensePlate1: toNullableText(transport.licensePlate1),
    vehicleRegistration: toNullableText(transport.vehicleRegistration),
    status: transport.status,
    pzNumbers: [],
  };
}

/**
 * Etap 2: `GET /api/supplier-orders/:id/transports` — the trimmed
 * "which trucks brought this ZD in" read that drives the PZ receipt picker.
 * Every optional field defaults to null/[] so a partially-populated transport
 * (no plate entered yet, no PZ issued yet) renders an honest "—" instead of
 * breaking the card.
 */
export function normalizeSupplierOrderTransports(
  raw: unknown,
  entity = 'supplierOrderTransports',
): SupplierOrderTransportDto[] {
  return asArray(raw, (item, index) => {
    const record = asRecord(item);

    if (!record) {
      reportDtoAnomaly(`${entity}[${index}]`, 'root', item);
    }

    return {
      id: asString(record?.id),
      routeNumber: asString(record?.routeNumber),
      transportCompany: asString(record?.transportCompany),
      loadingDate: asOptionalString(record?.loadingDate) ?? null,
      departureDate: asOptionalString(record?.departureDate) ?? null,
      // Both plate fields are optional in the response — an older backend
      // returns only `vehicleRegistration`, a newer one carries `licensePlate1`
      // as well. Absence of either stays null rather than ''.
      licensePlate1: asOptionalString(record?.licensePlate1) ?? null,
      vehicleRegistration: asOptionalString(record?.vehicleRegistration) ?? null,
      status: normalizeTransportStatus(record?.status, `${entity}[${index}].status`),
      pzNumbers: asArray(record?.pzNumbers, (pzNumber) => asString(pzNumber)).filter(Boolean),
    };
  }).filter((transport) => Boolean(transport.id));
}

function normalizeTransportProducts(raw: unknown, entity: string): TransportProductDto[] {
  return asArray(raw, (item, index) => {
    const record = asRecord(item);

    return {
      id: asString(record?.id),
      cargoItemId: asOptionalString(record?.cargoItemId),
      orderId: asOptionalString(record?.orderId),
      name: asString(record?.name),
      quantity: record?.quantity == null ? undefined : asNumber(record?.quantity),
      quantityPcs: record?.quantityPcs == null ? null : asNumber(record.quantityPcs),
      quantityM2: record?.quantityM2 == null ? null : asNumber(record.quantityM2),
      quantityM3: record?.quantityM3 == null ? null : asNumber(record.quantityM3),
      unit: asOptionalString(record?.unit),
      deliveredQuantity: record?.deliveredQuantity == null ? null : asNumber(record.deliveredQuantity),
      deliveredQuantityPcs: record?.deliveredQuantityPcs == null
        ? null
        : asNumber(record.deliveredQuantityPcs),
      deliveredQuantityM2: record?.deliveredQuantityM2 == null
        ? null
        : asNumber(record.deliveredQuantityM2),
      deliveredQuantityM3: record?.deliveredQuantityM3 == null
        ? null
        : asNumber(record.deliveredQuantityM3),
      commercialQuantity: record?.commercialQuantity == null ? null : asNumber(record.commercialQuantity),
      commercialUnit: asOptionalString(record?.commercialUnit) ?? null,
      supplierPurchaseValuePLN: record?.supplierPurchaseValuePLN == null ? null : asNumber(record.supplierPurchaseValuePLN),
      grossWeight: record?.grossWeight == null ? undefined : asNumber(record?.grossWeight),
      width: record?.width == null ? undefined : asNumber(record?.width),
      height: record?.height == null ? undefined : asNumber(record?.height),
      length: record?.length == null ? undefined : asNumber(record?.length),
      thickness: record?.thickness == null ? undefined : asNumber(record?.thickness),
      productCategoryDimensionDisplayProfile:
        asOptionalString(record?.productCategoryDimensionDisplayProfile),
      productVariantId: asOptionalString(record?.productVariantId) ?? null,
      catalogProductName: asOptionalString(record?.catalogProductName) ?? null,
      variantName: asOptionalString(record?.variantName) ?? null,
      variantAttributes: normalizeTransportVariantAttributes(record?.variantAttributes),
      variantIsDefault: record?.variantIsDefault == null
        ? undefined
        : asBoolean(record.variantIsDefault),
      catalogProductWidth: record?.catalogProductWidth == null
        ? undefined
        : asNumber(record.catalogProductWidth),
      catalogProductHeight: record?.catalogProductHeight == null
        ? undefined
        : asNumber(record.catalogProductHeight),
      catalogProductLength: record?.catalogProductLength == null
        ? undefined
        : asNumber(record.catalogProductLength),
      catalogProductThickness: record?.catalogProductThickness == null
        ? undefined
        : asNumber(record.catalogProductThickness),
    };
  });
}

function normalizeTransportVariantAttributes(raw: unknown): Record<string, string> | undefined {
  const record = asRecord(raw);
  if (!record) return undefined;

  const entries = Object.entries(record).flatMap(([rawKey, rawValue]) => {
    const key = rawKey.trim();
    const value = (
      typeof rawValue === 'string'
      || typeof rawValue === 'number'
      || typeof rawValue === 'boolean'
    ) ? String(rawValue).trim() : '';
    return key && value ? [[key, value] as const] : [];
  });

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

function normalizeTransportCargoItems(raw: unknown, entity: string): TransportCargoItemDto[] {
  return asArray(raw, (item, index) => {
    const record = asRecord(item);

    return {
      id: asString(record?.id),
      transportId: asString(record?.transportId),
      orderId: asString(record?.orderId),
      orderProductId: asOptionalString(record?.orderProductId),
      productName: asString(record?.productName),
      quantity: asNumber(record?.quantity),
      quantityPcs: record?.quantityPcs == null ? null : asNumber(record.quantityPcs),
      quantityM2: record?.quantityM2 == null ? null : asNumber(record.quantityM2),
      quantityM3: record?.quantityM3 == null ? null : asNumber(record.quantityM3),
      deliveredQuantity: record?.deliveredQuantity == null ? null : asNumber(record.deliveredQuantity),
      deliveredQuantityPcs: record?.deliveredQuantityPcs == null
        ? null
        : asNumber(record.deliveredQuantityPcs),
      deliveredQuantityM2: record?.deliveredQuantityM2 == null
        ? null
        : asNumber(record.deliveredQuantityM2),
      deliveredQuantityM3: record?.deliveredQuantityM3 == null
        ? null
        : asNumber(record.deliveredQuantityM3),
      deliveryConfirmedAt: asOptionalString(record?.deliveryConfirmedAt) ?? null,
      deliveryConfirmedById: asOptionalString(record?.deliveryConfirmedById) ?? null,
      unit: asOptionalString(record?.unit),
      commercialQuantity: record?.commercialQuantity == null ? null : asNumber(record.commercialQuantity),
      commercialUnit: asOptionalString(record?.commercialUnit) ?? null,
      supplierPurchaseValuePLN: record?.supplierPurchaseValuePLN == null ? null : asNumber(record.supplierPurchaseValuePLN),
      goodsValueTotal: record?.goodsValueTotal == null ? null : asNumber(record?.goodsValueTotal),
      manualAllocatedTransportCost: record?.manualAllocatedTransportCost == null
        ? null
        : asNumber(record?.manualAllocatedTransportCost),
      width: record?.width == null ? undefined : asNumber(record?.width),
      height: record?.height == null ? undefined : asNumber(record?.height),
      length: record?.length == null ? undefined : asNumber(record?.length),
      thickness: record?.thickness == null ? undefined : asNumber(record?.thickness),
      grossWeight: record?.grossWeight == null ? undefined : asNumber(record?.grossWeight),
      // Package-based LDM snapshot (migration 093). Null stays null so an
      // absent packaging value renders "—" rather than a fabricated 0.
      piecesPerPackage: record?.piecesPerPackage == null ? null : asNumber(record?.piecesPerPackage),
      packageLength: record?.packageLength == null ? null : asNumber(record?.packageLength),
      packageWidth: record?.packageWidth == null ? null : asNumber(record?.packageWidth),
      packageStackLayers: record?.packageStackLayers == null ? null : asNumber(record?.packageStackLayers),
      // F2 (migration 094): manual LDM override. Null stays null so an absent
      // override falls back to packaging rather than a fabricated 0.
      manualLdm: record?.manualLdm == null ? null : asNumber(record?.manualLdm),
    };
  });
}

function normalizeTransportCostAllocation(
  raw: unknown,
  entity: string,
): TransportCostAllocationResult | undefined {
  if (raw == null) {
    return undefined;
  }

  const record = asRecord(raw);
  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }

  return {
    method: normalizeTransportCostAllocationMethod(record?.method),
    items: normalizeTransportCostAllocationItems(record?.items, `${entity}.items`),
    summary: {
      totalGoodsValue: asNumber(asRecord(record?.summary)?.totalGoodsValue),
      totalAllocatedTransportCost: asNumber(asRecord(record?.summary)?.totalAllocatedTransportCost),
      totalLandedCost: asNumber(asRecord(record?.summary)?.totalLandedCost),
      allocationDifference: asNumber(asRecord(record?.summary)?.allocationDifference),
    },
    warnings: asArray(record?.warnings, (item, index) => {
      const warning = asRecord(item);
      const code = asString(warning?.code) as TransportCostAllocationWarningCode;
      // MISSING_DIMENSIONS is an aggregated warning carrying the offending
      // lines' itemId/name in warning.items[]; the detail page's renderer
      // shows them as a "Pozycje: <names>" suffix, same as the form. Dropping
      // this field silently emptied that suffix on TransportRouteDetail.
      const items = asOptionalArray(warning?.items, (warningItem) => {
        const warningItemRecord = asRecord(warningItem);
        return {
          itemId: asOptionalString(warningItemRecord?.itemId),
          name: asOptionalString(warningItemRecord?.name) ?? null,
        };
      });

      return {
        code,
        itemId: asOptionalString(warning?.itemId),
        ...(items ? { items } : {}),
      };
    }),
  };
}

function normalizeTransportCostAllocationItems(
  raw: unknown,
  entity: string,
): TransportCostAllocationResult['items'] {
  const rawItems = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? Object.values(raw) : [];

  return asArray(rawItems, (item, index) => {
    const record = asRecord(item);
    if (!record) {
      reportDtoAnomaly(`${entity}[${index}]`, 'root', item);
    }

    return {
      id: asOptionalString(record?.id) || asOptionalString(record?.itemId),
      quantity: asNumber(record?.quantity),
      unit: asOptionalString(record?.unit) ?? null,
      goodsValueTotal: asNumber(record?.goodsValueTotal),
      manualAllocatedTransportCost: asNumber(record?.manualAllocatedTransportCost),
      allocatedTransportCost: asNumber(record?.allocatedTransportCost),
      goodsCostPerUnit: record?.goodsCostPerUnit == null ? null : asNumber(record?.goodsCostPerUnit),
      transportCostPerUnit: record?.transportCostPerUnit == null ? null : asNumber(record?.transportCostPerUnit),
      landedCostTotal: asNumber(record?.landedCostTotal),
      landedCostPerUnit: record?.landedCostPerUnit == null ? null : asNumber(record?.landedCostPerUnit),
      // OSCAR (PDF-contract wave): per-metric unit costs computed server-side
      // by buildPerMetricUnitCosts (transportCostAllocation.js) — null stays
      // null (metric not computable for this line), never coerced to 0.
      transportCostPerAreaSqm: record?.transportCostPerAreaSqm == null ? null : asNumber(record?.transportCostPerAreaSqm),
      transportCostPerVolumeM3: record?.transportCostPerVolumeM3 == null ? null : asNumber(record?.transportCostPerVolumeM3),
      transportCostPerLdm: record?.transportCostPerLdm == null ? null : asNumber(record?.transportCostPerLdm),
    };
  });
}

function normalizeTransportCostAllocationMethod(value: unknown): TransportCostAllocationMethod {
  return TRANSPORT_COST_ALLOCATION_METHODS.includes(value as TransportCostAllocationMethod)
    ? value as TransportCostAllocationMethod
    : 'BY_COMMERCIAL_QTY';
}

function normalizeTransportStatus(value: unknown, entity: string): TransportRouteStatus {
  if (typeof value === 'string' && ROUTE_STATUSES.includes(value as TransportRouteStatus)) {
    return value as TransportRouteStatus;
  }

  reportDtoAnomaly(entity, 'value', value);
  return 'PLANNED';
}

function pickTransportCustomerName(
  record: ReturnType<typeof asRecord>,
  firstOrder: TransportOrderSummaryDto | undefined,
): string {
  if (!record) {
    return firstOrder?.customerName || '';
  }

  const rawCustomer = record.customer;

  if (typeof rawCustomer === 'string' && rawCustomer.trim()) {
    return rawCustomer;
  }

  return asOptionalString(record.customerName) || firstOrder?.customerName || '';
}
